// Pixel Studio: the pure helpers and option lists behind the Studio overlay
// (screens/pixel-studio.tsx). No React here.

import type {
  ConsentMode,
  EventToggle,
  InternalField,
  PiiField,
  PixelExtractPreviewResponse,
  PixelPiiChannel,
  Transform,
  WebEventType,
} from '@/api/types';

export const INTERNAL_FIELDS: readonly InternalField[] = ['production', 'show', 'source_reference', 'venue', 'event', 'ticket', 'order', 'currency', 'value', 'units'];
export const PII_FIELDS: readonly PiiField[] = ['email', 'phone', 'first_name', 'last_name', 'street1', 'city', 'state', 'zip'];
export const TRANSFORMS: readonly Transform[] = ['none', 'trim', 'lower', 'upper'];
export const CONSENT_MODES: readonly ConsentMode[] = ['always', 'opt_in', 'opt_out'];
export const FIRING_EVENTS: ReadonlyArray<keyof EventToggle> = ['purchase', 'pageview'];
export const BEACON_PROVIDERS: ReadonlyArray<keyof PixelExtractPreviewResponse['beacons']> = ['meta', 'ttd'];

// Event-scope options for a mapping/PII rule's "When" selector. '' = shared.
export const WHEN_OPTIONS: ReadonlyArray<{ value: '' | WebEventType; label: string }> = [
  { value: '', label: 'All events' },
  { value: 'Purchase', label: 'Purchase' },
  { value: 'Pageview', label: 'Pageview' },
];

export const PII_CHANNELS: ReadonlyArray<readonly [PixelPiiChannel, string]> = [
  ['email', 'Email'], ['phone', 'Phone'], ['first_name', 'First name'], ['last_name', 'Last name'],
  ['street1', 'Street'], ['city', 'City'], ['state', 'State'], ['zip5', 'ZIP'],
];

/**
 * The member of `options` equal to `value`, or `undefined`. A `<select>`'s
 * value is a plain string; this narrows it back to the option type it was
 * rendered from instead of casting.
 */
export function oneOf<T extends string>(options: readonly T[], value: string): T | undefined {
  return options.find((option) => option === value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

// Walk a JSON value → dot/bracket paths for autocomplete. Bounded in depth and
// count so a huge dataLayer can't blow up the suggestion list.
export function enumeratePaths(obj: unknown, prefix?: string, out?: string[], depth?: number): string[] {
  out = out || [];
  depth = depth || 0;
  if (depth > 6 || out.length > 400 || obj == null || typeof obj !== 'object') return out;
  if (Array.isArray(obj)) {
    obj.slice(0, 5).forEach((v: unknown, i) => {
      const p = (prefix || '') + '[' + i + ']';
      out.push(p);
      enumeratePaths(v, p, out, depth + 1);
    });
  } else if (isRecord(obj)) {
    Object.keys(obj).forEach((k) => {
      const p = prefix ? prefix + '.' + k : k;
      out.push(p);
      enumeratePaths(obj[k], p, out, depth + 1);
    });
  }
  return out;
}

export function maskValue(s: unknown): string {
  const text = String(s == null ? '' : s);
  if (!text) return '';
  return text[0] + '•'.repeat(Math.min(Math.max(text.length - 1, 0), 8));
}
export function truncHash(h: string | null | undefined): string { return h ? h.slice(0, 12) + '…' : ''; }
export function agoLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.now() - Date.parse(iso);
  if (isNaN(ms)) return '';
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return m + 'm ago';
  const h = Math.floor(m / 60);
  if (h < 24) return h + 'h ago';
  return Math.floor(h / 24) + 'd ago';
}
